"use client";

import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import MainLayout from "../sidebar/Sidebar.js";
import api from "../api/api"; // Assuming this is a custom API client
import {
  Hourglass,
  Loader2,
  CheckCircle,
  XCircle,
  Save,
  FileText,
} from "lucide-react";

export default function ReceiptProcessing() {
  const navigate = useNavigate();
  const location = useLocation();
  const [locationState, setLocation] = useState("Receipt Processing");

  // Get receipt data from navigation state
  const receiptData = location.state?.receipt;
  const receiptId = receiptData?.id;

  const [processingStatus, setProcessingStatus] = useState("pending");
  const [extractedText, setExtractedText] = useState("");
  const [receiptInfo, setReceiptInfo] = useState(null);
  const [error, setError] = useState("");
  const [pollingActive, setPollingActive] = useState(true);

  // Poll for processing status
  useEffect(() => {
    if (!receiptId || !pollingActive) return;

    const pollStatus = async () => {
      try {
        const response = await api.get(`/receipts/${receiptId}/status`);
        const data = response.data;
        setProcessingStatus(data.processing_status);
        setExtractedText(data.extracted_text || "");
        setReceiptInfo(data.receipt_data);

        // Stop polling if processing is complete or failed
        if (
          ["completed", "failed", "local_storage"].includes(
            data.processing_status
          )
        ) {
          setPollingActive(false);
        }
      } catch (err) {
        console.error("Error polling status:", err);
        setError("Failed to get processing status");
        setPollingActive(false);
      }
    };

    // Poll immediately, then every 3 seconds
    pollStatus();
    const interval = setInterval(pollStatus, 3000);
    return () => clearInterval(interval);
  }, [receiptId, pollingActive]);

  // Redirect if no receipt data
  if (!receiptData) {
    return (
      <MainLayout location={locationState} setLocation={setLocation}>
        <div className="flex flex-col items-center justify-center h-full p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">
            No Receipt Data
          </h2>
          <button
            onClick={() => navigate("/Dashboard")}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go to Dashboard
          </button>
        </div>
      </MainLayout>
    );
  }

  const getStatusDisplay = () => {
    switch (processingStatus) {
      case "pending":
        return {
          color: "text-yellow-600",
          bg: "bg-yellow-100",
          icon: <Hourglass className="size-6" />,
          title: "Upload Complete",
          message:
            "Your receipt has been uploaded successfully. Text extraction will begin shortly...",
        };
      case "processing":
        return {
          color: "text-blue-600",
          bg: "bg-blue-100",
          icon: <Loader2 className="size-6 animate-spin" />,
          title: "Processing Receipt",
          message:
            "Extracting text from your receipt using AI. This usually takes 30-60 seconds...",
        };
      case "completed":
        return {
          color: "text-green-600",
          bg: "bg-green-100",
          icon: <CheckCircle className="size-6" />,
          title: "Processing Complete",
          message: "Text has been successfully extracted from your receipt!",
        };
      case "failed":
        return {
          color: "text-red-600",
          bg: "bg-red-100",
          icon: <XCircle className="size-6" />,
          title: "Processing Failed",
          message:
            "Unable to extract text from this receipt. The image quality may be too low.",
        };
      case "local_storage":
        return {
          color: "text-gray-600",
          bg: "bg-gray-100",
          icon: <Save className="size-6" />,
          title: "Receipt Saved",
          message:
            "Receipt saved locally. Text extraction is not available at this time.",
        };
      default:
        return {
          color: "text-gray-600",
          bg: "bg-gray-100",
          icon: <FileText className="size-6" />,
          title: "Unknown Status",
          message: "Processing status unclear.",
        };
    }
  };

  const statusDisplay = getStatusDisplay();

  return (
    <MainLayout location={locationState} setLocation={setLocation}>
      <div className="h-full flex flex-col bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b p-6">
          <h1 className="text-2xl font-bold text-gray-900">
            Receipt Processing
          </h1>
          <p className="text-gray-600 mt-1">{receiptData.original_name}</p>
        </div>

        {/* Content */}
        <div className="flex-1 p-6 overflow-y-auto">
          <div className="max-w-4xl mx-auto space-y-6">
            {/* Status Card */}
            <div
              className={`${statusDisplay.bg} p-6 rounded-lg transition-colors duration-500 ease-in-out`}
            >
              <div
                className={`${statusDisplay.color} flex items-center space-x-3`}
              >
                <span className="text-2xl">{statusDisplay.icon}</span>
                <div>
                  <h3 className="text-lg font-semibold">
                    {statusDisplay.title}
                  </h3>
                  <p className="text-sm opacity-80">{statusDisplay.message}</p>
                </div>
              </div>
              {/* Loading animation for active processing */}
              {processingStatus === "processing" && (
                <div className="mt-4 w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                  {/* Modified progress bar for smoother pulse effect */}
                  <div className="h-full w-full rounded-full bg-gradient-to-r from-blue-300 via-blue-500 to-blue-300 animate-pulse"></div>
                </div>
              )}
            </div>

            {/* Receipt Preview */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Receipt Image
              </h3>
              {receiptData.s3_url ? (
                <img
                  src={receiptData.s3_url || "/placeholder.svg"}
                  alt="Receipt"
                  className="max-w-full h-auto rounded-lg border mx-auto"
                  style={{ maxHeight: "400px" }}
                />
              ) : (
                <div className="bg-gray-100 rounded-lg p-8 text-center">
                  <p className="text-gray-500">Receipt image not available</p>
                </div>
              )}
            </div>

            {/* Extracted Information */}
            {processingStatus === "completed" && receiptInfo && (
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Extracted Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {receiptInfo.merchant_name && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Merchant
                      </label>
                      <p className="mt-1 text-gray-900">
                        {receiptInfo.merchant_name}
                      </p>
                    </div>
                  )}
                  {receiptInfo.date && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Date
                      </label>
                      <p className="mt-1 text-gray-900">{receiptInfo.date}</p>
                    </div>
                  )}
                  {receiptInfo.total_amount && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Total Amount
                      </label>
                      <p className="mt-1 text-gray-900 font-semibold">
                        ${receiptInfo.total_amount.toFixed(2)}
                      </p>
                    </div>
                  )}
                  {receiptInfo.tax_amount && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Tax
                      </label>
                      <p className="mt-1 text-gray-900">
                        ${receiptInfo.tax_amount.toFixed(2)}
                      </p>
                    </div>
                  )}
                </div>

                {/* Line Items Section */}
                {receiptInfo.line_items &&
                  receiptInfo.line_items.length > 0 && (
                    <div className="mt-6">
                      <h4 className="text-md font-semibold text-gray-900 mb-3">
                        Line Items
                      </h4>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="space-y-3">
                          {receiptInfo.line_items.map((item, index) => (
                            <div
                              key={index}
                              className="bg-white rounded border p-3"
                            >
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <p className="font-medium text-gray-900">
                                    {item.name || "Unknown Item"}
                                  </p>
                                  {item.category && (
                                    <p className="text-sm text-gray-600">
                                      {item.category}
                                    </p>
                                  )}
                                  <div className="flex items-center space-x-4 mt-1 text-sm text-gray-700">
                                    {item.quantity && (
                                      <span>Qty: {item.quantity}</span>
                                    )}
                                    {item.unit_price && (
                                      <span>
                                        Unit: ${item.unit_price.toFixed(2)}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                {item.total_price && (
                                  <div className="text-right">
                                    <p className="font-semibold text-gray-900">
                                      ${item.total_price.toFixed(2)}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                {/* Debug Information */}
                {receiptInfo && receiptInfo.extraction_method && (
                  <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="text-sm font-semibold text-blue-900 mb-2">
                      Processing Details
                    </h4>
                    <div className="text-sm text-blue-800 space-y-1">
                      {receiptInfo.tables_detected !== undefined && (
                        <p>
                          <strong>Tables Detected:</strong>{" "}
                          {receiptInfo.tables_detected}
                        </p>
                      )}
                      {receiptInfo.extraction_method ===
                        "openai_with_tables" && (
                        <p className="text-green-700">
                          ✅ Enhanced table-based extraction used
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Raw Text */}
            {extractedText && (
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Extracted Text
                </h3>
                <div className="bg-gray-50 rounded-lg p-4 max-h-60 overflow-y-auto">
                  <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono">
                    {extractedText}
                  </pre>
                </div>
              </div>
            )}

            {/* Error Display */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800">{error}</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="bg-white border-t p-6">
          <div className="flex justify-between items-center">
            <button
              onClick={() => navigate("/receipt-capture")}
              className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Capture Another Receipt
            </button>
            <button
              onClick={() => navigate("/Dashboard")}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
